import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Play, Pencil, Trash2, Zap, History, Loader2, Bot, CheckCircle2, XCircle, Clock, Sparkles } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import ChatHint from "../components/ChatHint";
import EmptyStateCards from "../components/EmptyStateCards";

const READ_TOOLS = ["get_products", "get_product", "get_leads", "get_deals", "get_dashboard_metrics"];
const WRITE_TOOLS = ["create_or_update_product", "create_or_update_lead", "create_or_update_deal", "create_offer_link", "generate_image"];
const WEEKDAYS = [
  { v: 0, label: "Mo" }, { v: 1, label: "Di" }, { v: 2, label: "Mi" },
  { v: 3, label: "Do" }, { v: 4, label: "Fr" }, { v: 5, label: "Sa" }, { v: 6, label: "So" },
];

const STATUS_COLOR = {
  success: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  running: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  waiting_approval: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  blocked: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const emptyForm = {
  name: "", description: "", instructions: "",
  product_scope: "all", selected_product_ids: [],
  allowed_tools: ["get_products", "get_deals", "get_dashboard_metrics"],
  trigger: "manual",
  schedule: { kind: "interval", interval_minutes: 60, time: "09:00", weekdays: [] },
  approval_required: true,
  budget_limits: { max_runs_per_day: 10, max_images_per_run: 0 },
  model: "gpt",
  status: "active",
};

export default function Skills() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);
  const [logs, setLogs] = useState({ open: false, skill: null, items: [] });
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/skills");
      setSkills(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...emptyForm, ...s,
      schedule: { ...emptyForm.schedule, ...(s.schedule || {}) },
      budget_limits: { ...emptyForm.budget_limits, ...(s.budget_limits || {}) },
    });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/skills/${editing.id}`, form);
        toast.success("Skill aktualisiert");
      } else {
        await api.post("/skills", form);
        toast.success("Skill erstellt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    }
  };
  const toggle = async (s) => {
    try {
      await api.post(`/skills/${s.id}/toggle`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const removeSkill = async () => {
    try {
      await api.delete(`/skills/${confirmDel.id}`);
      toast.success("Skill gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const runNow = async (s) => {
    setRunningId(s.id);
    try {
      const { data } = await api.post(`/skills/${s.id}/run`);
      if (data.status === "success") toast.success("Skill ausgeführt");
      else if (data.status === "waiting_approval") toast("Wartet auf Zustimmung");
      else if (data.status === "blocked") toast.error(`Blockiert: ${data.error || ""}`);
      else if (data.status === "failed") toast.error(`Fehlgeschlagen: ${data.error || ""}`);
      load();
      if (logs.open && logs.skill?.id === s.id) openLogs(s);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lauf fehlgeschlagen");
    } finally {
      setRunningId(null);
    }
  };
  const openLogs = async (s) => {
    try {
      const { data } = await api.get(`/skills/${s.id}/logs`);
      setLogs({ open: true, skill: s, items: data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const stats = useMemo(() => ({
    active: skills.filter((s) => s.status === "active").length,
    scheduled: skills.filter((s) => s.trigger === "scheduled").length,
  }), [skills]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="skills-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Automation</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Skills</h1>
          <p className="text-sm text-slate-400 mt-2">
            {skills.length === 0 ? "Noch leer." : `${skills.length} Skills · ${stats.active} aktiv · ${stats.scheduled} geplant.`}
          </p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Lege einen Wochenreport-Skill an."'} /></div>
        </div>
        <Button onClick={openCreate} data-testid="add-skill-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neuer Skill
        </Button>
      </header>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Lade…</div>
          ) : skills.length === 0 ? (
            <EmptyStateCards
              testid="skills-empty"
              title="Noch keine Skills"
              subtitle="Ein Skill erledigt eine wiederkehrende Aufgabe für dich — zu einer Uhrzeit oder auf Knopfdruck."
              cards={[
                { icon: Plus, title: "Eigenen Skill anlegen", body: "Name + Anweisung + Zeitpunkt. Fertig.", onClick: openCreate, testid: "empty-card-create" },
                { icon: Bot, title: "Im Chat erstellen lassen", body: "Beschreibe die Aufgabe — der Agent legt den Skill an.", to: "/ki-agent", testid: "empty-card-chat" },
                { icon: Sparkles, title: "Neues Projekt mit Vorlage", body: "Starter-Skills aus einer Projekt-Vorlage anlegen.", to: "/alle-projekte", testid: "empty-card-template" },
              ]}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Skill</TableHead>
                  <TableHead className="text-slate-400">Trigger</TableHead>
                  <TableHead className="text-slate-400">Modell</TableHead>
                  <TableHead className="text-slate-400">Nächster Lauf</TableHead>
                  <TableHead className="text-slate-400">Aktiv</TableHead>
                  <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {skills.map((s) => (
                  <TableRow key={s.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`skill-row-${s.id}`}>
                    <TableCell>
                      <div className="font-medium text-white">{s.name}</div>
                      <div className="text-xs text-slate-500 truncate max-w-md">{s.description || s.instructions?.slice(0, 80)}</div>
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs">
                      <Badge variant="outline" className="border-white/10 text-slate-300 text-[10px]">
                        {s.trigger === "scheduled" ? scheduleLabel(s.schedule) : s.trigger}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300 text-xs font-mono">{s.model}</TableCell>
                    <TableCell className="text-slate-400 text-xs">
                      {s.trigger === "scheduled" && s.next_run_at
                        ? formatDistanceToNow(parseISO(s.next_run_at), { addSuffix: true, locale: de })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Switch checked={s.status === "active"} onCheckedChange={() => toggle(s)}
                        data-testid={`skill-toggle-${s.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => runNow(s)} disabled={runningId === s.id}
                        data-testid={`skill-run-${s.id}`} className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10">
                        {runningId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openLogs(s)}
                        data-testid={`skill-logs-${s.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <History className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)}
                        data-testid={`skill-edit-${s.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(s)}
                        data-testid={`skill-delete-${s.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SkillFormDialog
        open={open} onClose={() => setOpen(false)}
        editing={editing} form={form} setForm={setForm} onSubmit={submit}
      />

      <Sheet open={logs.open} onOpenChange={(o) => setLogs((prev) => ({ ...prev, open: o }))}>
        <SheetContent className="bg-[#0b0f17] border-white/10 text-slate-100 w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Verlauf: {logs.skill?.name}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-3" data-testid="skill-logs-panel">
            {logs.items.length === 0 ? (
              <div className="text-sm text-slate-500">Noch keine Läufe.</div>
            ) : logs.items.map((r) => (
              <LogRow key={r.id} log={r} />
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Skill löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              „{confirmDel?.name}" und alle Läufe werden entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skill-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={removeSkill} data-testid="skill-delete-confirm"
              className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LogRow({ log }) {
  return (
    <Card className="bg-[#111827] border-white/5" data-testid={`log-row-${log.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <Badge className={`${STATUS_COLOR[log.status] || STATUS_COLOR.blocked} border font-normal text-[10px]`}>
            {log.status === "success" && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
            {log.status === "failed" && <XCircle className="h-3 w-3 inline mr-1" />}
            {log.status === "waiting_approval" && <Clock className="h-3 w-3 inline mr-1" />}
            {log.status}
          </Badge>
          <span className="text-[11px] text-slate-500">
            {log.started_at && formatDistanceToNow(parseISO(log.started_at), { addSuffix: true, locale: de })} · {log.trigger_type}
          </span>
        </div>
        {log.error && <div className="text-xs text-red-400">Fehler: {log.error}</div>}
        {log.output && <div className="text-xs text-slate-300 whitespace-pre-wrap">{log.output.slice(0, 1200)}</div>}
        {(log.tool_calls || []).length > 0 && (
          <details className="text-[11px]">
            <summary className="text-slate-500 cursor-pointer hover:text-slate-300">Tool-Aufrufe ({log.tool_calls.length})</summary>
            <pre className="mt-1 p-2 rounded bg-black/40 border border-white/5 overflow-x-auto text-slate-400">
              {JSON.stringify(log.tool_calls, null, 2).slice(0, 2000)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function scheduleLabel(s) {
  if (!s) return "scheduled";
  const t = s?.time || "09:00";
  if (s?.kind === "interval") {
    const m = s.interval_minutes || 60;
    if (m % 60 === 0) return `Alle ${m / 60} Std.`;
    return `Alle ${m} Min.`;
  }
  if (s?.kind === "daily") return `Täglich um ${t}`;
  if (s?.kind === "weekly") {
    const names = (s.weekdays || []).map((d) => ({0:"Mo",1:"Di",2:"Mi",3:"Do",4:"Fr",5:"Sa",6:"So"})[d]).join(", ");
    return `Wöchentlich · ${names || "— Tag wählen"} · ${t}`;
  }
  if (s?.kind === "once") return "Einmalig";
  return s?.kind || "—";
}

function SkillFormDialog({ open, onClose, editing, form, setForm, onSubmit }) {
  const toggleTool = (t) =>
    setForm((p) => ({
      ...p,
      allowed_tools: p.allowed_tools.includes(t)
        ? p.allowed_tools.filter((x) => x !== t)
        : [...p.allowed_tools, t],
    }));
  const toggleWeekday = (d) =>
    setForm((p) => ({
      ...p,
      schedule: {
        ...p.schedule,
        weekdays: p.schedule.weekdays?.includes(d)
          ? p.schedule.weekdays.filter((x) => x !== d)
          : [...(p.schedule.weekdays || []), d],
      },
    }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="skill-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Skill bearbeiten" : "Neuer Skill"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Essentials — Name + Anweisung + Trigger + Zeitplan */}
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="skill-form-name" placeholder="z. B. Wochenreport" />
          </Field>

          <Field label="Was soll der Skill tun?" required>
            <Textarea rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} required
              data-testid="skill-form-instructions"
              placeholder="z. B. Fasse die aktuelle Pipeline zusammen und schlage 3 nächste Schritte vor." />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Wann läuft der Skill?">
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger data-testid="skill-form-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Auf Knopfdruck</SelectItem>
                  <SelectItem value="scheduled">Nach Zeitplan</SelectItem>
                  <SelectItem value="event">Bei Ereignis (bald)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.trigger === "scheduled" && (
              <Field label="Wie oft?">
                <Select value={form.schedule.kind} onValueChange={(v) => setForm({ ...form, schedule: { ...form.schedule, kind: v } })}>
                  <SelectTrigger data-testid="skill-form-schedule-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Täglich</SelectItem>
                    <SelectItem value="weekly">Wöchentlich</SelectItem>
                    <SelectItem value="interval">Alle X Minuten</SelectItem>
                    <SelectItem value="once">Einmalig</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          {form.trigger === "scheduled" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {form.schedule.kind === "interval" && (
                <Field label="Alle wie viele Minuten?">
                  <Input type="number" min="1" value={form.schedule.interval_minutes || 60}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, interval_minutes: Number(e.target.value) || 60 } })}
                    data-testid="skill-form-interval" />
                </Field>
              )}
              {(form.schedule.kind === "daily" || form.schedule.kind === "weekly") && (
                <Field label="Um welche Uhrzeit? (UTC)">
                  <Input type="time" value={form.schedule.time || "09:00"}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, time: e.target.value } })}
                    data-testid="skill-form-time" />
                </Field>
              )}
              {form.schedule.kind === "weekly" && (
                <Field label="An welchen Tagen?" className="md:col-span-2">
                  <div className="flex gap-1 flex-wrap">
                    {WEEKDAYS.map((d) => (
                      <button key={d.v} type="button" onClick={() => toggleWeekday(d.v)}
                        data-testid={`skill-form-weekday-${d.v}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                          form.schedule.weekdays?.includes(d.v)
                            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                            : "bg-transparent border-white/10 text-slate-400 hover:text-white"
                        }`}>{d.label}</button>
                    ))}
                  </div>
                </Field>
              )}
            </div>
          )}

          {/* Erweitert — Modell, Beschreibung, Tools, Budget, Approval-Switch */}
          <details className="group border-t border-white/5 pt-3">
            <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="skill-form-advanced-toggle">
              <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (Modell, Tools, Budget, Zustimmung)
            </summary>
            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Kurzbeschreibung">
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="skill-form-description" />
                </Field>
                <Field label="KI-Modell">
                  <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                    <SelectTrigger data-testid="skill-form-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt">GPT (OpenAI)</SelectItem>
                      <SelectItem value="claude">Claude (Anthropic)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Erlaubte Werkzeuge">
                <div className="space-y-2" data-testid="skill-form-tools">
                  <ToolSection title="Lesen (ohne Rückfrage)" items={READ_TOOLS} selected={form.allowed_tools} onToggle={toggleTool} />
                  <ToolSection title="Schreiben (benötigt Zustimmung)" items={WRITE_TOOLS} selected={form.allowed_tools} onToggle={toggleTool} />
                </div>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Max. Läufe pro Tag">
                  <Input type="number" min="1" value={form.budget_limits.max_runs_per_day}
                    onChange={(e) => setForm({ ...form, budget_limits: { ...form.budget_limits, max_runs_per_day: Number(e.target.value) || 1 } })}
                    data-testid="skill-form-max-runs" />
                </Field>
                <Field label="Max. Bilder pro Lauf">
                  <Input type="number" min="0" value={form.budget_limits.max_images_per_run}
                    onChange={(e) => setForm({ ...form, budget_limits: { ...form.budget_limits, max_images_per_run: Number(e.target.value) || 0 } })}
                    data-testid="skill-form-max-images" />
                </Field>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-slate-300 text-xs">Jede Schreib-Aktion zuerst zeigen</Label>
                  <div className="text-[11px] text-slate-500">Empfohlen an. Aus: der Skill schreibt direkt, ohne Rückfrage.</div>
                </div>
                <Switch checked={form.approval_required} onCheckedChange={(v) => setForm({ ...form, approval_required: v })}
                  data-testid="skill-form-approval-required" />
              </div>
            </div>
          </details>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="skill-form-cancel">Abbrechen</Button>
            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="skill-form-submit">
              {editing ? "Änderungen speichern" : "Skill anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToolSection({ title, items, selected, onToggle }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((t) => {
          const on = selected.includes(t);
          return (
            <button key={t} type="button" onClick={() => onToggle(t)}
              data-testid={`skill-form-tool-${t}`}
              className={`text-[11px] px-2.5 py-1 rounded border transition ${
                on ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                   : "bg-transparent border-white/10 text-slate-400 hover:text-white"
              }`}>{t}</button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-300 text-xs mb-1 block">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}
